import { useAppDispatch, useAppSelector } from "./useRedux";
import { useCallback, useMemo } from "react";
import { likeOrDislike } from "redux/slices/liked";
import { useAuth } from "contexts/auth/auth.context";
import { useMutation, useQueryClient } from "react-query";
import { LikeOptions } from "interfaces";
import likeService from "services/like";
import { error } from "components/alert/toast";

type Props = {
  type: "shop" | "product";
  itemId: number;
};

export default function useLiked({ type, itemId }: Props) {
  const dispatch = useAppDispatch();
  const queryClint = useQueryClient();

  const { isAuthenticated } = useAuth();
  const { shopList, productList } = useAppSelector((state) => state.liked);

  const isLiked = useMemo(() => {
    switch (type) {
      case "shop":
        return (
          !!shopList.length && shopList.some((item) => item.shopId === itemId)
        );

      case "product":
        return (
          !!productList.length &&
          productList.some((item) => item.productId === itemId)
        );

      default:
        return false;
    }
  }, [type, itemId, shopList, productList]);

  const { mutate: likeRequest } = useMutation({
    mutationFn: (body: LikeOptions) => likeService.like(body),
    onError: (err: any) => {
      error(err?.message);
    },
  });

  const { mutate: dislikeRequest } = useMutation({
    mutationFn: (body: LikeOptions) => likeService.disLike(body),
    onMutate: async (body) => {
      if (body.type === "product") {
        await queryClint.cancelQueries(["likedProducts"], { exact: false });
        const prevLikeList = queryClint.getQueryData(["likedProducts"], {
          exact: false,
        });
        queryClint.setQueriesData(
          { queryKey: ["likedProducts"], exact: false },
          (old: any) => {
            if (!old) return prevLikeList;
            return {
              ...old,
              pages: old?.pages?.map((page: any) => ({
                ...page,
                data: page?.data?.filter(
                  (product: any) => product?.id !== body.type_id,
                ),
              })),
            };
          },
        );

        return { prevLikeList };
      }
    },
    onError: (err: any) => {
      error(err?.message);
    },
  });

  const handleLocalLikeOrDislike = useCallback(() => {
    dispatch(
      likeOrDislike({
        type,
        [`${type}Id`]: itemId,
        sent: isAuthenticated,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, itemId, isAuthenticated]);

  const handleLikeOrDislike = useCallback(() => {
    if (isAuthenticated) {
      if (isLiked) {
        dislikeRequest({ type, type_id: itemId });
      } else {
        likeRequest({ type, type_id: itemId });
      }
    }
    handleLocalLikeOrDislike();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, itemId, isAuthenticated, isLiked]);

  return {
    isLiked,
    handleLikeOrDislike,
  };
}
